import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Phone,
  Users,
  Calendar,
  LogOut,
  Loader2,
  RefreshCw,
  Settings,
  MessageSquare,
  AlertTriangle,
  Clock,
  Shield,
  Crown,
  Building2,
  Globe
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

// Import tab components
import LeadsTab from '@/components/dashboard/LeadsTab';
import MessagesTab from '@/components/dashboard/MessagesTab';
import SettingsTab from '@/components/dashboard/SettingsTab';
import CalendarTab from '@/components/dashboard/CalendarTab';
import ContractorTab from '@/components/dashboard/ContractorTab';
import WebsiteTab from '@/components/dashboard/WebsiteTab';
import UpgradeModal from '@/components/dashboard/UpgradeModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Tier configuration
const tierConfig = {
  demo: { 
    label: 'Demo', 
    color: 'text-orange-400', 
    bg: 'bg-orange-500/10', 
    border: 'border-orange-500/30',
    icon: Clock 
  },
  standard: { 
    label: 'Standard', 
    color: 'text-sky-400', 
    bg: 'bg-sky-500/10', 
    border: 'border-sky-500/30',
    icon: Shield 
  },
  premium: { 
    label: 'Premium', 
    color: 'text-yellow-400', 
    bg: 'bg-yellow-500/10', 
    border: 'border-yellow-500/30',
    icon: Crown 
  }
};

// Feature access by tier
const featureAccess = {
  demo: ['leads', 'stats', 'calendar', 'messages'],
  standard: ['leads', 'stats', 'calendar', 'messages'],
  premium: ['leads', 'stats', 'calendar', 'messages', 'google_calendar', 'reviews', 'analytics', 'priority_support']
};

export default function ClientDashboard() {
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Subscription state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradingTo, setUpgradingTo] = useState(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('leads');
  
  // Messages tab state
  const [conversations, setConversations] = useState([]);
  const [calls, setCalls] = useState([]);
  const [commStats, setCommStats] = useState(null);
  const [expandedConversation, setExpandedConversation] = useState(null);
  
  // Settings tab state
  const [twilioSettings, setTwilioSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [calendarStatus, setCalendarStatus] = useState(null);
  const [connectingCalendar, setConnectingCalendar] = useState(false);
  
  // Account editing state
  const [editingAccount, setEditingAccount] = useState(false);
  const [accountForm, setAccountForm] = useState({ email: '', business_name: '' });
  const [savingAccount, setSavingAccount] = useState(false);
  
  // Privacy notice state
  const [privacyNoticeDismissed, setPrivacyNoticeDismissed] = useState(() => {
    return localStorage.getItem('privacyNoticeDismissed') === 'true';
  });
  
  // Admin state
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Calendar tab state
  const [appointments, setAppointments] = useState([]);
  const [appointmentStats, setAppointmentStats] = useState(null);
  const [showNewAppointment, setShowNewAppointment] = useState(false);
  const [newAppointment, setNewAppointment] = useState({
    title: '',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    service_type: '',
    start_time: '',
    end_time: '',
    notes: ''
  });
  const [savingAppointment, setSavingAppointment] = useState(false);

  useEffect(() => {
    fetchData();
    checkAdminStatus();
    
    const params = new URLSearchParams(window.location.search);
    let shouldCleanUrl = false;
    
    if (params.get('calendar_connected') === 'true') {
      toast.success('Google Calendar connected successfully!');
      setActiveTab('settings');
      shouldCleanUrl = true;
    }
    if (params.get('tab') === 'settings') {
      setActiveTab('settings');
    }
    if (params.get('token')) {
      shouldCleanUrl = true;
    }
    
    if (shouldCleanUrl) {
      window.history.replaceState({}, '', window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount
  
  const checkAdminStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/check`, {
        withCredentials: true
      });
      setIsAdmin(response.data.is_admin);
    } catch {
      setIsAdmin(false);
    }
  };

  // Fetch all data on mount and when client changes for specific tabs
  useEffect(() => {
    if (client?.customer_id) {
      if (activeTab === 'settings') {
        fetchTwilioSettings(client.customer_id);
        fetchCalendarStatus(client.customer_id);
      } else if (activeTab === 'calendar') {
        fetchAppointments(client.customer_id);
      }
    }
  }, [client?.customer_id, activeTab]);

  const fetchData = async () => {
    try {
      const clientRes = await axios.get(`${BACKEND_URL}/api/client/me`, {
        withCredentials: true
      });
      setClient(clientRes.data);
      
      fetchCommunicationData();

      const statsRes = await axios.get(`${BACKEND_URL}/api/client/stats`, {
        withCredentials: true
      });
      setStats(statsRes.data);

      const leadsRes = await axios.get(`${BACKEND_URL}/api/client/leads?limit=20`, {
        withCredentials: true
      });
      setLeads(leadsRes.data.leads || []);

    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        navigate('/client/login');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const fetchCommunicationData = async () => {
    try {
      const [messagesRes, callsRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/client/messages?limit=100`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/client/calls?limit=50`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/client/communication-stats`, { withCredentials: true })
      ]);
      
      setConversations(messagesRes.data.conversations || []);
      setCalls(callsRes.data.calls || []);
      setCommStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching communication data:', error);
    }
  };
  
  const fetchTwilioSettings = async (customerId) => {
    try {
      if (!customerId) return;
      
      const response = await axios.get(
        `${BACKEND_URL}/api/webhook/twilio/customer/${customerId}/settings`,
        { withCredentials: true }
      );
      setTwilioSettings(response.data);
    } catch (error) {
      console.error('Error fetching Twilio settings:', error);
      setTwilioSettings({
        call_recording_enabled: false,
        sms_notifications_enabled: true,
        phone_service_active: false
      });
    }
  };
  
  const fetchCalendarStatus = async (customerId) => {
    try {
      if (!customerId) return;
      
      const customerRes = await axios.get(`${BACKEND_URL}/api/client/me`, {
        withCredentials: true
      });
      
      setCalendarStatus({
        connected: !!customerRes.data.google_calendar_connected_at,
        email: customerRes.data.google_calendar_email || 'Connected',
        connected_at: customerRes.data.google_calendar_connected_at
      });
    } catch (error) {
      console.error('Error fetching calendar status:', error);
      setCalendarStatus({ connected: false });
    }
  };
  
  // Subscription tier helpers
  const currentTier = client?.subscription_tier || 'demo';
  const tierInfo = tierConfig[currentTier] || tierConfig.demo;
  const TierIcon = tierInfo.icon;
  
  const hasFeatureAccess = (feature) => {
    const tier = client?.subscription_tier || 'demo';
    return featureAccess[tier]?.includes(feature) || false;
  };
  
  const isDemo = () => currentTier === 'demo';
  
  const handleUpgrade = async (targetTier) => {
    setUpgradingTo(targetTier);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/payments/checkout`, {
        package_id: targetTier === 'premium' ? 'premium_maintenance' : 'standard_maintenance',
        origin_url: window.location.origin,
        customer_email: client?.email,
        metadata: { customer_id: client?.customer_id, upgrade: 'true' }
      }, { withCredentials: true });
      
      if (response.data.checkout_url || response.data.url) {
        window.location.href = response.data.checkout_url || response.data.url;
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      toast.error('Failed to start upgrade. Please try again.');
      setUpgradingTo(null);
    }
  };
  
  const handleConnectCalendar = async () => {
    setConnectingCalendar(true);
    try {
      const redirectUrl = `${window.location.origin}/client/dashboard?tab=settings`;
      const response = await axios.get(
        `${BACKEND_URL}/api/calendar/auth/connect`,
        {
          params: {
            customer_id: client?.customer_id,
            redirect_url: redirectUrl
          },
          withCredentials: true
        }
      );
      
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      console.error('Error connecting calendar:', error);
      toast.error(error.response?.data?.detail || 'Failed to connect calendar');
      setConnectingCalendar(false);
    }
  };
  
  const handleDisconnectCalendar = async () => {
    try {
      await axios.delete(
        `${BACKEND_URL}/api/calendar/auth/disconnect/${client?.customer_id}`,
        { withCredentials: true }
      );
      setCalendarStatus({ connected: false });
      toast.success('Google Calendar disconnected');
    } catch (error) {
      console.error('Error disconnecting calendar:', error);
      toast.error('Failed to disconnect calendar');
    }
  };
  
  // Account editing functions
  const startEditingAccount = () => {
    setAccountForm({
      email: client?.email || '',
      business_name: client?.business_name || ''
    });
    setEditingAccount(true);
  };
  
  const cancelEditingAccount = () => {
    setEditingAccount(false);
    setAccountForm({ email: '', business_name: '' });
  };
  
  const saveAccountInfo = async () => {
    if (!accountForm.email || !accountForm.business_name) {
      toast.error('Email and business name are required');
      return;
    }
    
    setSavingAccount(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/client/profile`,
        {
          email: accountForm.email,
          business_name: accountForm.business_name
        },
        { withCredentials: true }
      );
      
      setClient(prev => ({
        ...prev,
        email: accountForm.email,
        business_name: accountForm.business_name
      }));
      
      setEditingAccount(false);
      toast.success('Account information updated');
    } catch (error) {
      console.error('Error updating account:', error);
      toast.error(error.response?.data?.detail || 'Failed to update account');
    } finally {
      setSavingAccount(false);
    }
  };
  
  // Calendar functions
  const fetchAppointments = async (customerId) => {
    try {
      if (!customerId) return;
      
      const [appointmentsRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/appointments/${customerId}/upcoming?days=30`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/appointments/${customerId}/stats?days=30`, { withCredentials: true })
      ]);
      
      setAppointments(appointmentsRes.data.appointments || []);
      setAppointmentStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    }
  };
  
  const handleCreateAppointment = async () => {
    if (!newAppointment.title || !newAppointment.customer_name || !newAppointment.customer_phone || !newAppointment.start_time) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setSavingAppointment(true);
    try {
      const startDate = new Date(newAppointment.start_time);
      const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
      
      await axios.post(
        `${BACKEND_URL}/api/appointments/${client?.customer_id}`,
        {
          ...newAppointment,
          end_time: newAppointment.end_time || endDate.toISOString()
        },
        { withCredentials: true }
      );
      
      toast.success('Appointment created!');
      setShowNewAppointment(false);
      setNewAppointment({
        title: '',
        customer_name: '',
        customer_phone: '',
        customer_email: '',
        service_type: '',
        start_time: '',
        end_time: '',
        notes: ''
      });
      fetchAppointments(client?.customer_id);
    } catch (error) {
      console.error('Error creating appointment:', error);
      toast.error(error.response?.data?.detail || 'Failed to create appointment');
    } finally {
      setSavingAppointment(false);
    }
  };
  
  const handleCancelAppointment = async (appointmentId) => {
    if (!confirm('Are you sure you want to cancel this appointment?')) return;
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/appointments/${client?.customer_id}/${appointmentId}`,
        { withCredentials: true }
      );
      toast.success('Appointment cancelled');
      fetchAppointments(client?.customer_id);
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      toast.error('Failed to cancel appointment');
    }
  };
  
  const formatAppointmentTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  
  const updateTwilioSetting = async (setting, value) => {
    setSavingSettings(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/webhook/twilio/customer/${client?.customer_id}/settings`,
        null,
        {
          params: { [setting]: value },
          withCredentials: true
        }
      );
      
      setTwilioSettings(prev => ({ ...prev, [setting]: value }));
      toast.success('Setting updated successfully');
    } catch (error) {
      console.error('Error updating setting:', error);
      toast.error('Failed to update setting');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/client/logout`, {}, {
        withCredentials: true
      });
      toast.success('Logged out');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear any local storage that might persist login state
      localStorage.removeItem('clientSession');
      sessionStorage.clear();
      // Force hard redirect with logout flag to prevent auto-login
      window.location.href = '/client/login?logged_out=true';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };
  
  // Calculate demo expiration
  const getDemoExpiration = () => {
    if (!client?.subscription_start_date || currentTier !== 'demo') return null;
    const startDate = new Date(client.subscription_start_date);
    const expirationDate = new Date(startDate);
    expirationDate.setMonth(expirationDate.getMonth() + 6);
    const daysLeft = Math.ceil((expirationDate - new Date()) / (1000 * 60 * 60 * 24));
    return { expirationDate, daysLeft };
  };
  
  const demoExpiration = getDemoExpiration();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Upgrade Modal */}
      <UpgradeModal
        show={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        currentTier={currentTier}
        upgradingTo={upgradingTo}
        handleUpgrade={handleUpgrade}
      />

      {/* Header */}
      <header className="sticky top-0 z-10 bg-slate-950/95 backdrop-blur border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center">
              <Phone className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg flex items-center gap-2">
                {client?.business_name || 'Alaska AI Services'}
                <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${tierInfo.bg} ${tierInfo.border} border`}>
                  <TierIcon className={`w-3 h-3 ${tierInfo.color}`} />
                  <span className={tierInfo.color}>{tierInfo.label}</span>
                </div>
              </h1>
              <p className="text-sm text-slate-400">{client?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin')}
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                data-testid="admin-panel-btn"
              >
                <Shield className="w-4 h-4 mr-2" />
                Admin
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              className="border-slate-700 text-slate-300"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="border-slate-700 text-slate-300"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Demo Expiration Warning Banner */}
        {isDemo() && demoExpiration && demoExpiration.daysLeft <= 30 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <div>
                <p className="font-medium text-amber-400">Demo Period Ending Soon</p>
                <p className="text-sm text-slate-300">
                  Your demo account expires in {demoExpiration.daysLeft} days. Upgrade now to keep your AI assistant running.
                </p>
              </div>
            </div>
            <Button
              onClick={() => setShowUpgradeModal(true)}
              className="bg-amber-500 hover:bg-amber-600 text-black"
              data-testid="demo-upgrade-btn"
            >
              Upgrade Now
            </Button>
          </motion.div>
        )}
        
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8 flex-wrap">
          <Button
            variant={activeTab === 'leads' ? 'default' : 'outline'}
            onClick={() => setActiveTab('leads')}
            className={activeTab === 'leads' ? 'bg-sky-500 hover:bg-sky-600' : 'border-slate-700'}
            data-testid="leads-tab-btn"
          >
            <Users className="w-4 h-4 mr-2" />
            Leads
          </Button>
          <Button
            variant={activeTab === 'messages' ? 'default' : 'outline'}
            onClick={() => setActiveTab('messages')}
            className={activeTab === 'messages' ? 'bg-sky-500 hover:bg-sky-600' : 'border-slate-700'}
            data-testid="messages-tab-btn"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Messages
          </Button>
          <Button
            variant={activeTab === 'calendar' ? 'default' : 'outline'}
            onClick={() => setActiveTab('calendar')}
            className={activeTab === 'calendar' ? 'bg-sky-500 hover:bg-sky-600' : 'border-slate-700'}
            data-testid="calendar-tab-btn"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Calendar
          </Button>
          <Button
            variant={activeTab === 'website' ? 'default' : 'outline'}
            onClick={() => setActiveTab('website')}
            className={activeTab === 'website' ? 'bg-sky-500 hover:bg-sky-600' : 'border-slate-700'}
            data-testid="website-tab-btn"
          >
            <Globe className="w-4 h-4 mr-2" />
            Website
          </Button>
          <Button
            variant={activeTab === 'contractor' ? 'default' : 'outline'}
            onClick={() => setActiveTab('contractor')}
            className={activeTab === 'contractor' ? 'bg-sky-500 hover:bg-sky-600' : 'border-slate-700'}
            data-testid="contractor-tab-btn"
          >
            <Building2 className="w-4 h-4 mr-2" />
            Contractor
            {client?.subscription_tier === 'premium' && (
              <Crown className="w-3 h-3 ml-1 text-yellow-400" />
            )}
          </Button>
          <Button
            variant={activeTab === 'settings' ? 'default' : 'outline'}
            onClick={() => setActiveTab('settings')}
            className={activeTab === 'settings' ? 'bg-sky-500 hover:bg-sky-600' : 'border-slate-700'}
            data-testid="settings-tab-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
        </div>
        
        {/* Tab Content */}
        {activeTab === 'leads' && (
          <LeadsTab
            stats={stats}
            leads={leads}
            client={client}
            navigate={navigate}
            formatDate={formatDate}
          />
        )}
        
        {activeTab === 'messages' && (
          <MessagesTab
            commStats={commStats}
            conversations={conversations}
            calls={calls}
            expandedConversation={expandedConversation}
            setExpandedConversation={setExpandedConversation}
            formatDate={formatDate}
          />
        )}
        
        {activeTab === 'website' && (
          <WebsiteTab
            customerId={client?.customer_id}
          />
        )}
        
        {activeTab === 'contractor' && (
          <ContractorTab
            client={client}
            hasFeatureAccess={hasFeatureAccess}
            setShowUpgradeModal={setShowUpgradeModal}
            stats={stats}
          />
        )}
        
        {activeTab === 'settings' && (
          <SettingsTab
            client={client}
            twilioSettings={twilioSettings}
            savingSettings={savingSettings}
            updateTwilioSetting={updateTwilioSetting}
            calendarStatus={calendarStatus}
            connectingCalendar={connectingCalendar}
            handleConnectCalendar={handleConnectCalendar}
            handleDisconnectCalendar={handleDisconnectCalendar}
            editingAccount={editingAccount}
            accountForm={accountForm}
            setAccountForm={setAccountForm}
            startEditingAccount={startEditingAccount}
            cancelEditingAccount={cancelEditingAccount}
            saveAccountInfo={saveAccountInfo}
            savingAccount={savingAccount}
            privacyNoticeDismissed={privacyNoticeDismissed}
            setPrivacyNoticeDismissed={setPrivacyNoticeDismissed}
            hasFeatureAccess={hasFeatureAccess}
            setShowUpgradeModal={setShowUpgradeModal}
          />
        )}
        
        {activeTab === 'calendar' && (
          <CalendarTab
            appointments={appointments}
            appointmentStats={appointmentStats}
            showNewAppointment={showNewAppointment}
            setShowNewAppointment={setShowNewAppointment}
            newAppointment={newAppointment}
            setNewAppointment={setNewAppointment}
            savingAppointment={savingAppointment}
            handleCreateAppointment={handleCreateAppointment}
            handleCancelAppointment={handleCancelAppointment}
            formatAppointmentTime={formatAppointmentTime}
            calendarStatus={calendarStatus}
          />
        )}
      </main>
    </div>
  );
}
